import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import moment from 'moment-timezone';
import AuthContext from '../../contexts/auth-context';
import { getQueuesAPI } from '../../util';

export default function GolfEventLocationController({ children, locationData, locationId }) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(true);

  // Current data state - keyed by locationId (live cards, only used when isToday)
  const [currentData, setCurrentData] = useState({});

  // Detail view state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [detailDailyData, setDetailDailyData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isToday, setIsToday] = useState(false);
  const [isFutureDate, setIsFutureDate] = useState(false);
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);

  // Derived from locationData
  const minDate = locationData?.calendarMinDate;
  const maxDate = locationData?.calendarMaxDate;
  const enabledDates = locationData?.calendarEnabledDates;
  const locations = locationData?.locations || [];
  const grandstands = locations.filter(l => l.type === 'grandstand');
  const markets = locations.filter(l => l.type === 'market');
  const gates = locations.filter(l => l.type === 'gate');
  const hospitalityVenues = locations.filter(l => l.type === 'hospitality');

  // Initialize selectedDate based on location's timezone and calendar constraints
  useEffect(() => {
    if (!locationData || selectedDate !== null) return;

    const tz = locationData.timeZone || 'America/New_York';
    const currentDateInTz = moment.tz(tz).format('YYYY-MM-DD');
    const isInRange = currentDateInTz >= minDate && currentDateInTz <= maxDate;
    const isEnabled = enabledDates?.includes(currentDateInTz);

    if (isInRange && isEnabled) {
      const todayDate = moment.tz(currentDateInTz + ' 12:00', 'YYYY-MM-DD HH:mm', tz).toDate();
      setSelectedDate(todayDate);
      setIsToday(true);
      setIsFutureDate(false);
      setAutoFetchEnabled(true);
    } else {
      const minDateObj = moment.tz(minDate + ' 12:00', 'YYYY-MM-DD HH:mm', tz).toDate();
      setSelectedDate(minDateObj);
      setIsToday(false);
      setIsFutureDate(minDate > currentDateInTz);
      setAutoFetchEnabled(false);
    }
  }, [locationData, selectedDate]);

  // Recalculate isToday and isFutureDate when selectedDate changes
  useEffect(() => {
    if (!selectedDate || !locationData) return;

    const tz = locationData.timeZone || 'America/New_York';
    const currentDateInTz = moment.tz(tz).format('YYYY-MM-DD');
    const selectedDateStr = moment(selectedDate).tz(tz).format('YYYY-MM-DD');

    const isTodayCheck = currentDateInTz === selectedDateStr;
    setIsToday(isTodayCheck);
    setIsFutureDate(selectedDateStr > currentDateInTz);
    setAutoFetchEnabled(isTodayCheck);
  }, [selectedDate, locationData]);

  // Format selectedDate to YYYY-MM-DD string in the location's timezone
  const getFormattedDate = useCallback(() => {
    if (!selectedDate || !locationData) return null;
    const tz = locationData.timeZone || 'America/New_York';
    return moment(selectedDate).tz(tz).format('YYYY-MM-DD');
  }, [selectedDate, locationData]);

  // Build locations payload for API calls
  const buildLocationsPayload = useCallback((locs) => {
    return locs.map(loc => ({
      locationId: loc.locationId,
      maxValue: loc.maxValue,
      type: loc.type,
      sensorIds: loc.sensorIds,
      doorCounterSensorIds: loc.doorCounterSensorIds || null,
      dwellTime: loc.dwellTime || null
    }));
  }, []);

  // Fetch current data for all locations (lightweight, for live cards)
  const fetchCurrentData = useCallback(() => {
    if (!locationData || !authCtx.loggedIn) return Promise.resolve({});

    return new Promise((resolve) => {
      getQueuesAPI("get-enterprise-golf-event-current-data",
        {
            measurement: locationData.measurement,
            doorMeasurement: locationData.doorMeasurement,
            dwellMeasurement: locationData.dwellMeasurement,
            org: authCtx.org,
            timezone: locationData.timeZone,
            locations: buildLocationsPayload(locations)
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
            }
            resolve({});
          } else if (data) {
            resolve(data);
          } else {
            resolve({});
          }
        },
        authCtx.sessionToken
      );
    });
  }, [locationData, authCtx, navigate, locations, buildLocationsPayload]);

  // Fetch daily data for a single location (for detail view)
  const fetchDailyDataForLocation = useCallback((location) => {
    if (!locationData || !authCtx.loggedIn || !selectedDate) return Promise.resolve(null);

    const formattedDate = getFormattedDate();
    if (!formattedDate) return Promise.resolve(null);

    return new Promise((resolve) => {
      getQueuesAPI("get-enterprise-golf-event-daily-data",
        {
            measurement: locationData.measurement,
            doorMeasurement: locationData.doorMeasurement,
            dwellMeasurement: locationData.dwellMeasurement,
            org: authCtx.org,
            date: formattedDate,
            timezone: locationData.timeZone,
            locations: buildLocationsPayload([location])
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
            }
            resolve(null);
          } else if (data) {
            resolve(data[String(location.locationId)] || null);
          } else {
            resolve(null);
          }
        },
        authCtx.sessionToken
      );
    });
  }, [locationData, authCtx, navigate, selectedDate, getFormattedDate, buildLocationsPayload]);

  // Open detail view - fetches daily data for the selected location
  const openLocationDetail = useCallback(async (location) => {
    setSelectedLocation(location);
    setDetailLoading(true);
    setDetailDailyData(null);

    const daily = await fetchDailyDataForLocation(location);
    setDetailDailyData(daily);
    setDetailLoading(false);
  }, [fetchDailyDataForLocation]);

  // Close detail view
  const closeLocationDetail = useCallback(() => {
    setSelectedLocation(null);
    setDetailDailyData(null);
    setDetailLoading(false);
  }, []);

  // Main data fetch (only fetches current data for live view)
  const fetchAllData = useCallback(async () => {
    if (!authCtx.loggedIn) {
      navigate('/auth');
      return;
    }
    if (!selectedDate) return;

    setLoading(true);

    if (isToday) {
      const current = await fetchCurrentData();
      setCurrentData(current);
    } else {
      setCurrentData({});
    }

    setLoading(false);
  }, [authCtx, navigate, selectedDate, isToday, fetchCurrentData]);

  // Initial fetch and refetch on date change
  useEffect(() => {
    if (authCtx && locationData && selectedDate) {
      fetchAllData();
    }
  }, [fetchAllData, selectedDate, authCtx, locationData]);

  // Close detail view on date change (data would be stale)
  useEffect(() => {
    if (selectedLocation) {
      closeLocationDetail();
    }
  }, [selectedDate]);

  // Auto-refresh current data every 60 seconds (only when viewing today)
  useEffect(() => {
    let interval;
    if (autoFetchEnabled && isToday && authCtx && locationData && selectedDate) {
     interval = setInterval(async () => {
        const current = await fetchCurrentData();
        setCurrentData(current);
    }, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoFetchEnabled, isToday, fetchCurrentData, authCtx, locationData, selectedDate]);

  // Manual refresh
  const refreshData = useCallback(async () => {
    if (!authCtx || !locationData || !selectedDate) return;
    setLoading(true);

    if (isToday) {
      const current = await fetchCurrentData();
      setCurrentData(current);
    }

    setLoading(false);

    // If detail is open, refresh that too
    if (selectedLocation) {
      setDetailLoading(true);
      const daily = await fetchDailyDataForLocation(selectedLocation);
      setDetailDailyData(daily);
      setDetailLoading(false);
    }
  }, [fetchCurrentData, fetchDailyDataForLocation, authCtx, locationData, selectedDate, selectedLocation, isToday]);

  return children({
    loading,
    locationData,
    locations,
    grandstands,
    markets,
    gates,
    hospitalityVenues,
    currentData,

    // Detail view
    selectedLocation,
    detailDailyData,
    detailLoading,
    openLocationDetail,
    closeLocationDetail,

    isToday,
    isFutureDate,
    autoFetchEnabled,
    setAutoFetchEnabled,
    refreshData,
    authCtx,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    enabledDates,
    showCalendar,
    setShowCalendar
  });
}